<template>
  <!-- data-clarity-mask: ver nota en FormularioSeguro.vue -->
  <v-card class="iedmt-card mx-auto" max-width="760" elevation="6" data-clarity-mask="true">
    <!-- Cabecera -->
    <div class="iedmt-header pa-5">
      <h2 class="iedmt-title mb-1">{{ t.titulo }}</h2>
      <p class="iedmt-subtitle mb-0">{{ t.subtitulo }}</p>
    </div>

    <v-card-text class="pa-5">
      <!-- ===================== PASO 1: SITUACIÓN DEL VEHÍCULO ==================== -->
      <div class="paso-label">{{ t.paso1 }}</div>
      <v-btn-toggle
        v-model="situacion"
        mandatory
        class="mb-6 d-flex flex-wrap"
        active-class="tipo-activo"
      >
        <v-btn value="usado" class="flex-grow-1">
          <v-icon left>mdi-car-back</v-icon>{{ t.usado }}
        </v-btn>
        <v-btn value="nuevo" class="flex-grow-1">
          <v-icon left>mdi-car-sports</v-icon>{{ t.nuevo }}
        </v-btn>
      </v-btn-toggle>

      <v-row dense class="mb-2">
        <v-col cols="12">
          <v-select
            v-model="territorio"
            :items="territorios"
            :item-text="idiomaActual === 'cat' ? 'nombre_cat' : 'nombre_es'"
            item-value="id"
            :label="t.territorio"
            outlined dense
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <!-- Territorios que no calculamos: IGIC / IPSI en lugar de IVA -->
      <v-alert
        v-if="territorioEspecial"
        type="info"
        dense text
        class="mt-3 mb-0"
      >
        {{ t.avisoTerritorio }}
        <div class="mt-2">
          <v-btn small color="primary" :href="enlaceWhatsapp" target="_blank" rel="noopener">
            <v-icon left small>mdi-whatsapp</v-icon>{{ t.ctaWhatsapp }}
          </v-btn>
        </div>
      </v-alert>

      <template v-if="!territorioEspecial">
        <!-- ===================== PASO 2: EL VEHÍCULO ===================== -->
        <div class="paso-label mt-6">{{ t.paso2 }}</div>

        <!-- Selector marca / modelo. Solo aparece si el catálogo del BOE
             está cargado (ver src/content/boeAnexo1.js). -->
        <template v-if="hayCatalogo && situacion === 'usado'">
          <v-row dense>
            <v-col cols="12" sm="5">
              <v-autocomplete
                v-model="marca"
                :items="marcas"
                :label="t.marca"
                outlined dense
                clearable
                :no-data-text="t.sinResultados"
                @change="modeloId = null"
              />
            </v-col>
            <v-col cols="12" sm="7">
              <v-autocomplete
                v-model="modeloId"
                :items="modelos"
                item-text="texto"
                item-value="id"
                :label="t.modelo"
                :disabled="!marca"
                outlined dense
                clearable
                :no-data-text="t.sinResultados"
                :hint="t.modeloHint"
                persistent-hint
                @change="aplicarModelo"
              />
            </v-col>
          </v-row>

          <div class="text-right mb-2 mt-1">
            <v-btn x-small text color="secondary" @click="modoManual = !modoManual">
              {{ modoManual ? t.volverCatalogo : t.noEncuentroModelo }}
            </v-btn>
          </div>
        </template>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="valorBoe"
              :label="situacion === 'usado' ? t.valorBoe : t.precioNuevo"
              suffix="€"
              type="number"
              min="0"
              outlined dense
              :readonly="usandoCatalogo"
              :hint="situacion === 'usado' ? t.valorBoeHint : t.precioNuevoHint"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="co2"
              :label="t.co2"
              suffix="g/km"
              type="number"
              min="0"
              outlined dense
              :disabled="co2Desconocido"
              :hint="t.co2Hint"
              persistent-hint
            />
          </v-col>
        </v-row>

        <v-checkbox
          v-model="co2Desconocido"
          class="mt-1"
          dense
          hide-details
        >
          <template #label>
            <span class="nota-text">{{ t.co2Desconocido }}</span>
          </template>
        </v-checkbox>

        <!-- Fecha de primera matriculación (solo usados) -->
        <v-row dense class="mt-3" v-if="situacion === 'usado'">
          <v-col cols="6" sm="4">
            <v-select
              v-model="mesMatriculacion"
              :items="meses"
              item-text="texto"
              item-value="valor"
              :label="t.mes"
              outlined dense
            />
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="anioMatriculacion"
              :items="anios"
              :label="t.anio"
              outlined dense
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              :value="coeficienteDepreciacion() + ' %'"
              :label="t.coeficiente"
              outlined dense readonly
              :hint="t.coeficienteHint"
              persistent-hint
            />
          </v-col>
        </v-row>

        <!-- ===================== PASO 3: EXENCIONES ===================== -->
        <div class="paso-label mt-6">{{ t.paso3 }}</div>
        <v-select
          v-model="exencion"
          :items="t.exenciones"
          item-text="texto"
          item-value="valor"
          :label="t.exencion"
          outlined dense
          :hint="t.exencionHint"
          persistent-hint
        />

        <v-alert v-if="errorInput" type="warning" dense text class="mt-4 mb-0">
          {{ errorInput }}
        </v-alert>

        <!-- Botón calcular -->
        <div class="text-center mt-5" v-if="!mostrarFormulario && !resultadoEnviado">
          <v-btn color="primary" large class="px-8 iedmt-cta" @click="calcular">
            {{ t.calcular }}
          </v-btn>
        </div>

        <!-- ===================== PASO 4: DATOS DE CONTACTO (GATE) ================= -->
        <v-expand-transition>
          <div v-if="mostrarFormulario && !resultadoEnviado">
            <v-divider class="my-5" />
            <div class="paso-label">{{ t.paso4 }}</div>
            <p class="gate-text mb-4">{{ t.gateText }}</p>

            <v-form ref="leadForm" v-model="formValido">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="lead.nombre"
                    :label="t.nombre"
                    :rules="[r.requerido]"
                    outlined dense
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="lead.telefono"
                    :label="t.telefono"
                    :rules="[r.requerido, r.telefono]"
                    outlined dense
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="lead.email"
                    :label="t.email"
                    :rules="[r.requerido, r.email]"
                    outlined dense
                  />
                </v-col>
              </v-row>

              <v-checkbox
                v-model="lead.consentimiento"
                :rules="[r.aceptar]"
                class="mt-0"
                dense
              >
                <template #label>
                  <span class="consent-text">{{ t.consent }}</span>
                </template>
              </v-checkbox>

              <!-- Campo trampa antispam: invisible para el usuario -->
              <input
                v-model="honeypot"
                class="gr-hp"
                type="text"
                name="_gotcha"
                tabindex="-1"
                autocomplete="off"
                aria-hidden="true"
              >

              <captcha-turnstile
                ref="captcha"
                :site-key="captchaSiteKey"
                :lang="idiomaActual === 'cat' ? 'ca' : 'es'"
                @token="onCaptchaToken"
              />

              <v-alert v-if="errorEnvio" type="error" dense text class="mb-3">
                {{ errorEnvio }}
              </v-alert>

              <div class="text-center">
                <v-btn
                  color="primary"
                  large
                  class="px-8 iedmt-cta"
                  :loading="enviando"
                  @click="enviar"
                >
                  {{ t.verResultado }}
                </v-btn>
              </div>
            </v-form>
          </div>
        </v-expand-transition>

        <!-- ===================== CONFIRMACIÓN ===================== -->
        <v-expand-transition>
          <div v-if="resultadoEnviado" class="mt-4">
            <div class="confirm-box pa-6 text-center">
              <v-icon size="56" color="#900A30" class="mb-3">mdi-email-check-outline</v-icon>
              <h3 class="confirm-titulo mb-2">{{ t.confirmTitulo }}</h3>
              <p class="confirm-text mb-1">
                {{ t.confirmText }} <strong>{{ lead.email }}</strong>.
              </p>
              <p class="confirm-sub mb-0">{{ t.confirmSub }}</p>
            </div>

            <div class="text-center mt-4">
              <v-btn text color="secondary" @click="reiniciar">
                <v-icon left>mdi-refresh</v-icon>{{ t.otroCalculo }}
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </template>

      <!-- Nota legal permanente -->
      <p class="nota-legal mt-6 mb-0">
        {{ t.notaLegal }}
        <a :href="ordenBoe.url" target="_blank" rel="noopener">{{ ordenBoe.referencia }}</a>.
      </p>
    </v-card-text>
  </v-card>
</template>

<script>
import captcha from '@/mixins/captcha'
import {
  ORDEN_BOE,
  VEHICULOS_BOE,
  DEPRECIACION_ANEXO_IV,
  DEPRECIACION_RESTO,
  marcasDisponibles,
  modelosDeMarca
} from '@/content/boeAnexo1'

/* =============================================================================
   VALORES REGULATORIOS — revisar cada enero
   =============================================================================
   Tipos del Impuesto Especial sobre Determinados Medios de Transporte (IEDMT),
   art. 70 de la Ley 38/1992. Epígrafes de turismos según emisiones de CO2.
   Sin cambios para 2026.
   ========================================================================== */
const TRAMOS_CO2 = {
  peninsula: [
    { hasta: 120,      tipo: 0 },
    { hasta: 160,      tipo: 4.75 },
    { hasta: 200,      tipo: 9.75 },
    { hasta: Infinity, tipo: 14.75 }
  ],
  canarias: [
    { hasta: 120,      tipo: 0 },
    { hasta: 160,      tipo: 3.75 },
    { hasta: 200,      tipo: 8.75 },
    { hasta: Infinity, tipo: 13.75 }
  ]
}

/* Tipo de IVA aplicable en península y Baleares. Interviene en el descuento del
   importe residual de impuestos indirectos de la base imponible. */
const TIPO_IVA = 21

export default {
  name: 'CalculadoraMatriculacion',

  mixins: [captcha],

  props: {
    /* Endpoint de Formspree donde llegan los leads. */
    formspreeEndpoint: {
      type: String,
      default: 'https://formspree.io/f/mlgqpqkw'
    },
    /* Número de WhatsApp general de la gestoría, sin espacios ni signos. */
    whatsapp: { type: String, default: '34639086934' },
    /* Idioma: 'es' o 'cat'. */
    idioma: { type: String, default: 'es' }
  },

  data() {
    const anioActual = new Date().getFullYear()
    return {
      idiomaActual: this.idioma === 'cat' ? 'cat' : 'es',

      situacion: 'usado',
      territorio: 'peninsula',
      marca: null,
      modeloId: null,
      modoManual: false,
      valorBoe: null,
      co2: null,
      co2Desconocido: false,
      mesMatriculacion: 1,
      anioMatriculacion: anioActual - 3,
      exencion: 'ninguna',

      mostrarFormulario: false,
      resultadoEnviado: false,
      enviando: false,
      errorInput: '',
      errorEnvio: '',
      formValido: false,

      lead: { nombre: '', email: '', telefono: '', consentimiento: false },
      res: {},

      ordenBoe: ORDEN_BOE,

      territorios: [
        { id: 'peninsula', nombre_es: 'Península y Baleares', nombre_cat: 'Península i Balears' },
        { id: 'canarias',  nombre_es: 'Canarias',             nombre_cat: 'Canàries' },
        { id: 'ceutamelilla', nombre_es: 'Ceuta y Melilla',   nombre_cat: 'Ceuta i Melilla' }
      ],

      textos: {
        es: {
          titulo: 'Calcula el impuesto de matriculación',
          subtitulo: 'Modelo 576 (IEDMT) para coches importados. Valor fiscal del BOE, emisiones de CO2 y antigüedad · Tipos vigentes 2026.',
          paso1: '1. Situación del vehículo',
          usado: 'Usado, ya matriculado fuera',
          nuevo: 'Nuevo, sin matricular',
          territorio: 'Territorio donde se matricula',
          avisoTerritorio: 'En Canarias, Ceuta y Melilla el impuesto no se calcula igual: en lugar del IVA intervienen el IGIC o el IPSI, y los tipos son distintos. Escríbenos y te damos la cifra exacta para tu caso.',
          ctaWhatsapp: 'Consultar por WhatsApp',
          paso2: '2. Datos del vehículo',
          marca: 'Marca',
          modelo: 'Modelo',
          modeloHint: 'Rellenamos el valor fiscal y las emisiones automáticamente',
          sinResultados: 'Sin resultados',
          noEncuentroModelo: 'No encuentro mi modelo',
          volverCatalogo: 'Volver al catálogo del BOE',
          valorBoe: 'Valor fiscal del vehículo a nuevo (BOE)',
          valorBoeHint: 'Precio medio de venta del Anexo I, IVA incluido',
          precioNuevo: 'Precio de adquisición (IVA incluido)',
          precioNuevoHint: 'Lo que figura en la factura de compra',
          co2: 'Emisiones de CO2',
          co2Hint: 'Apartado V.7 de la ficha técnica o del COC',
          co2Desconocido: 'No consta el dato de CO2 (se aplica el tipo máximo)',
          mes: 'Mes 1ª matriculación',
          anio: 'Año 1ª matriculación',
          coeficiente: 'Valor que conserva',
          coeficienteHint: 'Anexo IV del BOE',
          paso3: '3. Exenciones',
          exencion: '¿Se puede aplicar alguna exención?',
          exencionHint: 'Toda exención hay que solicitarla a Hacienda antes de matricular',
          exenciones: [
            { valor: 'ninguna',     texto: 'Ninguna — matriculación ordinaria' },
            { valor: 'traslado',    texto: 'Traslado de residencia habitual a España' },
            { valor: 'discapacidad',texto: 'Persona con discapacidad o movilidad reducida' },
            { valor: 'taxi',        texto: 'Taxi, VTC o autoescuela' },
            { valor: 'alquiler',    texto: 'Alquiler sin conductor' }
          ],
          errValor: 'Introduce el valor fiscal del vehículo para continuar.',
          errCo2: 'Introduce las emisiones de CO2 o marca que no constan.',
          calcular: 'Calcular mi impuesto',
          paso4: '4. Recibe el resultado por email',
          gateText: 'Introduce tus datos y te enviamos el desglose completo del impuesto a tu correo. Sin compromiso.',
          nombre: 'Nombre y apellidos', telefono: 'Teléfono', email: 'Email',
          consent: 'Acepto la política de privacidad y que Gestoría Riola me contacte sobre este trámite.',
          verResultado: 'Recibir el cálculo por email',
          confirmTitulo: '¡Datos recibidos!',
          confirmText: 'Te enviaremos el desglose del impuesto de matriculación a',
          confirmSub: 'Revisa tu bandeja de entrada (y la carpeta de spam) en los próximos minutos. En Gestoría Riola confirmamos el valor fiscal, presentamos el modelo 576 y tramitamos la matriculación completa por ti.',
          otroCalculo: 'Hacer otro cálculo',
          errEnvio: 'No hemos podido enviar el cálculo. Inténtalo de nuevo o contáctanos por WhatsApp.',
          captchaEspera: 'Estamos comprobando que no eres un robot. Espera un segundo y vuelve a darle al botón.',
          notaLegal: 'Cálculo orientativo. La base imponible definitiva la fija la Agencia Tributaria a partir de los precios medios de venta aprobados por la',
          rq: 'Campo obligatorio', re: 'Email no válido', rt: 'Teléfono no válido', ra: 'Debes aceptar para continuar'
        },

        cat: {
          titulo: "Calcula l'impost de matriculació",
          subtitulo: 'Model 576 (IEDMT) per a cotxes importats. Valor fiscal del BOE, emissions de CO2 i antiguitat · Tipus vigents 2026.',
          paso1: '1. Situació del vehicle',
          usado: 'Usat, ja matriculat fora',
          nuevo: 'Nou, sense matricular',
          territorio: 'Territori on es matricula',
          avisoTerritorio: "A Canàries, Ceuta i Melilla l'impost no es calcula igual: en lloc de l'IVA hi intervenen l'IGIC o l'IPSI, i els tipus són diferents. Escriu-nos i et donem la xifra exacta per al teu cas.",
          ctaWhatsapp: 'Consultar per WhatsApp',
          paso2: '2. Dades del vehicle',
          marca: 'Marca',
          modelo: 'Model',
          modeloHint: 'Omplim el valor fiscal i les emissions automàticament',
          sinResultados: 'Sense resultats',
          noEncuentroModelo: 'No trobo el meu model',
          volverCatalogo: 'Tornar al catàleg del BOE',
          valorBoe: 'Valor fiscal del vehicle a nou (BOE)',
          valorBoeHint: "Preu mitjà de venda de l'Annex I, IVA inclòs",
          precioNuevo: "Preu d'adquisició (IVA inclòs)",
          precioNuevoHint: 'El que consta a la factura de compra',
          co2: 'Emissions de CO2',
          co2Hint: 'Apartat V.7 de la fitxa tècnica o del COC',
          co2Desconocido: 'No consta la dada de CO2 (s\'aplica el tipus màxim)',
          mes: 'Mes 1a matriculació',
          anio: 'Any 1a matriculació',
          coeficiente: 'Valor que conserva',
          coeficienteHint: 'Annex IV del BOE',
          paso3: '3. Exempcions',
          exencion: 'Es pot aplicar alguna exempció?',
          exencionHint: "Tota exempció s'ha de sol·licitar a Hisenda abans de matricular",
          exenciones: [
            { valor: 'ninguna',     texto: 'Cap — matriculació ordinària' },
            { valor: 'traslado',    texto: 'Trasllat de residència habitual a Espanya' },
            { valor: 'discapacidad',texto: 'Persona amb discapacitat o mobilitat reduïda' },
            { valor: 'taxi',        texto: 'Taxi, VTC o autoescola' },
            { valor: 'alquiler',    texto: 'Lloguer sense conductor' }
          ],
          errValor: 'Introdueix el valor fiscal del vehicle per continuar.',
          errCo2: 'Introdueix les emissions de CO2 o marca que no consten.',
          calcular: 'Calcular el meu impost',
          paso4: '4. Rep el resultat per email',
          gateText: "Introdueix les teves dades i t'enviem el desglossament complet de l'impost al teu correu. Sense compromís.",
          nombre: 'Nom i cognoms', telefono: 'Telèfon', email: 'Email',
          consent: 'Accepto la política de privacitat i que Gestoria Riola em contacti sobre aquest tràmit.',
          verResultado: 'Rebre el càlcul per email',
          confirmTitulo: 'Dades rebudes!',
          confirmText: "T'enviarem el desglossament de l'impost de matriculació a",
          confirmSub: "Revisa la teva safata d'entrada (i la carpeta de correu brossa) en els pròxims minuts. A Gestoria Riola confirmem el valor fiscal, presentem el model 576 i tramitem la matriculació completa per tu.",
          otroCalculo: 'Fer un altre càlcul',
          errEnvio: "No hem pogut enviar el càlcul. Torna-ho a provar o contacta'ns per WhatsApp.",
          captchaEspera: 'Estem comprovant que no ets un robot. Espera un segon i torna a prémer el botó.',
          notaLegal: "Càlcul orientatiu. La base imposable definitiva la fixa l'Agència Tributària a partir dels preus mitjans de venda aprovats per la",
          rq: 'Camp obligatori', re: 'Email no vàlid', rt: 'Telèfon no vàlid', ra: "Has d'acceptar per continuar"
        }
      }
    }
  },

  computed: {
    t() { return this.textos[this.idiomaActual] },

    hayCatalogo() { return VEHICULOS_BOE.length > 0 },

    marcas() { return marcasDisponibles() },

    modelos() { return this.marca ? modelosDeMarca(this.marca) : [] },

    /* El valor es de solo lectura mientras venga del catálogo oficial. */
    usandoCatalogo() {
      return this.hayCatalogo && !this.modoManual && !!this.modeloId && this.situacion === 'usado'
    },

    territorioEspecial() {
      return this.territorio !== 'peninsula'
    },

    enlaceWhatsapp() {
      const msg = this.idiomaActual === 'cat'
        ? "Hola! Vull calcular l'impost de matriculació d'un cotxe importat."
        : 'Hola, quiero calcular el impuesto de matriculación de un coche importado.'
      return 'https://wa.me/' + this.whatsapp + '?text=' + encodeURIComponent(msg)
    },

    anios() {
      const actual = new Date().getFullYear()
      const arr = []
      for (let a = actual; a >= 1980; a--) arr.push(a)
      return arr
    },

    meses() {
      const es = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
      const cat = ['Gener','Febrer','Març','Abril','Maig','Juny','Juliol','Agost','Setembre','Octubre','Novembre','Desembre']
      const nombres = this.idiomaActual === 'cat' ? cat : es
      return nombres.map((texto, i) => ({ valor: i + 1, texto }))
    },

    r() {
      const t = this.t
      return {
        requerido: v => !!v || t.rq,
        email: v => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) || t.re,
        telefono: v => /^[0-9+\s]{6,15}$/.test(v || '') || t.rt,
        aceptar: v => !!v || t.ra
      }
    }
  },

  methods: {
    eur(n) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: 2
      }).format(n || 0)
    },

    /* Al elegir un modelo del catálogo, arrastramos valor y emisiones. */
    aplicarModelo(id) {
      const encontrado = this.modelos.find(m => m.id === id)
      if (!encontrado) return
      this.valorBoe = encontrado.vehiculo.valor
      if (encontrado.vehiculo.co2) {
        this.co2 = encontrado.vehiculo.co2
        this.co2Desconocido = false
      }
    },

    /* Anexo IV: porcentaje del valor a nuevo que conserva el vehículo. */
    coeficienteDepreciacion() {
      if (this.situacion === 'nuevo') return 100
      const hoy = new Date()
      const matric = new Date(this.anioMatriculacion, (this.mesMatriculacion || 1) - 1, 1)
      const anios = (hoy - matric) / (365.25 * 24 * 3600 * 1000)
      for (const tramo of DEPRECIACION_ANEXO_IV) {
        if (anios < tramo.hastaAnios) return tramo.porcentaje
      }
      return DEPRECIACION_RESTO
    },

    /* Epígrafe aplicable según emisiones. Sin dato de CO2 va al tramo máximo. */
    tipoImpositivo() {
      const tabla = this.territorio === 'canarias' ? TRAMOS_CO2.canarias : TRAMOS_CO2.peninsula
      if (this.co2Desconocido) return tabla[tabla.length - 1].tipo
      const emisiones = this.co2 || 0
      for (const tramo of tabla) {
        if (emisiones < tramo.hasta) return tramo.tipo
      }
      return tabla[tabla.length - 1].tipo
    },

    calcular() {
      this.errorInput = ''
      if (!this.valorBoe || this.valorBoe <= 0) {
        this.errorInput = this.t.errValor
        return
      }
      if (!this.co2Desconocido && (this.co2 === null || this.co2 === '' || this.co2 < 0)) {
        this.errorInput = this.t.errCo2
        return
      }
      this.computar()
      this.mostrarFormulario = true
    },

    /* -------------------------------------------------------------------------
       Cálculo del IEDMT

       Usado ya matriculado en el extranjero (art. 69 Ley 38/1992):
         valor de mercado = valor BOE a nuevo × coeficiente Anexo IV
         del valor de mercado se descuenta el importe residual de los impuestos
         indirectos que ya iban incluidos en él:
             base = valor de mercado / (1 + IVA% + tipo IEDMT%)
         cuota = base × tipo IEDMT%

       Nuevo sin matricular:
         base  = precio de adquisición sin IVA
         cuota = base × tipo IEDMT%
       ---------------------------------------------------------------------- */
    computar() {
      const tipo = this.tipoImpositivo()
      const coef = this.coeficienteDepreciacion()
      const exento = this.exencion !== 'ninguna'

      let valorMercado
      let base

      if (this.situacion === 'usado') {
        valorMercado = this.valorBoe * (coef / 100)
        base = valorMercado / (1 + (TIPO_IVA / 100) + (tipo / 100))
      } else {
        valorMercado = this.valorBoe
        base = this.valorBoe / (1 + (TIPO_IVA / 100))
      }

      const cuota = exento ? 0 : base * (tipo / 100)

      this.res = {
        situacion: this.situacion,
        valorBoe: this.valorBoe,
        coef,
        valorMercado,
        co2: this.co2Desconocido ? null : this.co2,
        tipo,
        base,
        cuota,
        exento,
        exencion: this.exencion
      }
    },

    async enviar() {
      this.errorEnvio = ''

      /* Campo trampa relleno: es un bot. Fingimos que ha ido bien. */
      if (this.esSpam()) {
        this.mostrarFormulario = false
        this.resultadoEnviado = true
        return
      }

      if (!this.$refs.leadForm.validate()) return

      if (this.captchaPendiente()) {
        this.errorEnvio = this.t.captchaEspera
        return
      }

      this.computar()
      this.enviando = true

      const etiquetaExencion = (this.t.exenciones.find(e => e.valor === this.exencion) || {}).texto

      const payload = {
        _subject: 'Nuevo cálculo de impuesto de matriculación desde la web',
        Nombre: this.lead.nombre,
        Email: this.lead.email,
        Telefono: this.lead.telefono,
        Situacion: this.situacion === 'usado' ? 'Usado importado' : 'Nuevo sin matricular',
        Territorio: 'Península y Baleares',
        Marca: this.marca || '(no indicada)',
        Modelo: this.modeloId
          ? ((this.modelos.find(m => m.id === this.modeloId) || {}).texto || '')
          : '(introducido a mano)',
        Primera_matriculacion: this.situacion === 'usado'
          ? `${this.mesMatriculacion}/${this.anioMatriculacion}`
          : '(vehículo nuevo)',
        Valor_BOE_a_nuevo: this.eur(this.res.valorBoe),
        Coeficiente_Anexo_IV: this.res.coef + ' %',
        Valor_de_mercado: this.eur(this.res.valorMercado),
        CO2: this.res.co2 === null ? 'No consta (tipo máximo)' : this.res.co2 + ' g/km',
        Tipo_IEDMT: this.res.tipo + ' %',
        Base_imponible: this.eur(this.res.base),
        Exencion: etiquetaExencion,
        CUOTA_estimada: this.res.exento
          ? '0,00 € (exención pendiente de reconocimiento por Hacienda)'
          : this.eur(this.res.cuota)
      }

      try {
        const resp = await fetch(this.formspreeEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({}, payload, this.camposAntispam()))
        })
        if (!resp.ok) throw new Error('bad response')
        this.mostrarFormulario = false
        this.resultadoEnviado = true
      } catch (e) {
        this.errorEnvio = this.t.errEnvio
      } finally {
        this.enviando = false
        this.reiniciarCaptcha()
      }
    },

    reiniciar() {
      this.resultadoEnviado = false
      this.mostrarFormulario = false
      this.marca = null
      this.modeloId = null
      this.modoManual = false
      this.valorBoe = null
      this.co2 = null
      this.co2Desconocido = false
      this.exencion = 'ninguna'
      this.lead = { nombre: '', email: '', telefono: '', consentimiento: false }
      this.res = {}
    }
  },

  watch: {
    idioma(v) { this.idiomaActual = v === 'cat' ? 'cat' : 'es' },
    situacion() { this.errorInput = '' },
    territorio() { this.errorInput = '' }
  }
}
</script>

<style scoped>
.iedmt-card { border-radius: 14px; overflow: hidden; }

.iedmt-header { background: #900A30; color: #fff; }
.iedmt-title { font-size: 1.35rem; font-weight: 700; line-height: 1.2; }
.iedmt-subtitle { font-size: 0.85rem; opacity: 0.92; }

.paso-label { color: #900A30; font-weight: 700; font-size: 0.95rem; margin-bottom: 12px; }

.tipo-activo { background: #900A30 !important; color: #fff !important; }

.iedmt-cta { font-weight: 700; letter-spacing: 0.3px; }

.gate-text, .consent-text { color: #37454A; font-size: 0.9rem; }
.nota-text { color: #37454A; font-size: 0.85rem; }

.confirm-box {
  background: #f7f2f4;
  border: 1px solid #e6d5db;
  border-radius: 12px;
  color: #37454A;
}
.confirm-titulo { color: #900A30; font-size: 1.35rem; font-weight: 800; }
.confirm-text { font-size: 1rem; color: #37454A; }
.confirm-sub { font-size: 0.82rem; color: #6b7478; line-height: 1.45; max-width: 520px; margin: 0 auto; }

.nota-legal { font-size: 0.75rem; color: #6b7478; line-height: 1.45; }
.nota-legal a { color: #900A30; }

.v-btn.primary { background-color: #900A30 !important; }

/* ---------------------------------------------------------------------------
   Móvil

   Las etiquetas del selector de situación son largas y Vuetify no parte el
   texto dentro de los v-btn. En pantalla estrecha se apilan y el texto salta
   de línea en lugar de salirse por el lado.
   ------------------------------------------------------------------------ */
.iedmt-card >>> .v-btn-toggle .v-btn__content {
  white-space: normal;
  line-height: 1.25;
}

@media (max-width: 600px) {
  .iedmt-title { font-size: 1.18rem; }
  .iedmt-subtitle { font-size: 0.8rem; }
  .iedmt-header { padding: 16px !important; }

  .iedmt-card >>> .v-btn-toggle {
    flex-direction: column;
    width: 100%;
  }

  .iedmt-card >>> .v-btn-toggle .v-btn {
    width: 100%;
    height: auto !important;
    min-height: 46px;
    padding: 10px 12px;
    border-radius: 6px !important;
  }

  .iedmt-card >>> .v-btn-toggle .v-btn:not(:last-child) {
    margin-bottom: 8px;
  }

  .iedmt-cta {
    width: 100%;
    height: auto !important;
    min-height: 48px;
  }

  .iedmt-cta >>> .v-btn__content {
    white-space: normal;
    line-height: 1.3;
  }

  .confirm-titulo { font-size: 1.18rem; }
  .confirm-text { font-size: 0.95rem; }
}
</style>
